// Simple className utility for Tailwind v4 (no external deps needed)
export function cn(...classes: (string | undefined | null | false)[]) {
  return classes.filter(Boolean).join(' ');
}

/**
 * Parse a datetime-local input value as local time.
 *
 * datetime-local inputs return strings like "2026-01-19T22:37" (no timezone).
 * new Date() behavior varies by browser - some treat as UTC, some as local.
 * This function explicitly parses as local time for consistency.
 */
export function parseDateTimeLocal(datetimeLocal: string): Date {
  const [datePart, timePart] = datetimeLocal.split('T');
  const [year, month, day] = datePart.split('-').map(Number);
  const [hours, minutes] = timePart.split(':').map(Number);
  return new Date(year, month - 1, day, hours, minutes);
}

/**
 * Format a Date object as a datetime-local input value.
 * Returns string like "2026-01-19T22:37" for use in datetime-local inputs.
 */
export function formatDateTimeLocal(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

/**
 * Calculate end time from a datetime-local start time and duration in minutes.
 * Returns a Date object representing the end time in local timezone.
 */
export function calculateEndTime(startTime: string, durationMinutes: number): Date | null {
  if (!startTime || durationMinutes <= 0) return null;

  const startDate = parseDateTimeLocal(startTime);
  return new Date(startDate.getTime() + durationMinutes * 60 * 1000);
}

/**
 * Calculate end time and return as datetime-local string.
 * Convenience function for use with datetime-local inputs.
 */
export function calculateEndTimeString(startTime: string, durationMinutes: number): string {
  const endDate = calculateEndTime(startTime, durationMinutes);
  return endDate ? formatDateTimeLocal(endDate) : '';
}